import {Ref, unref} from 'vue';
import {PulseTimeAllocation} from '../../types/DataTypes.ts';

export function useDragHandler(
  localAllocation: Ref<PulseTimeAllocation>,
  enabledCategories: Ref<any[]>,
  disabled: Ref<boolean | undefined>,
  barRef: Ref<HTMLElement | undefined>,
  emit: (event: 'update-allocation', key: string, value: number) => void
) {
  function onDragStart(event: MouseEvent, segmentIndex: number, isRightEdge: boolean) {
    if (unref(disabled)) return;
    
    event.preventDefault();
    const categories = unref(enabledCategories);
    const allocation = unref(localAllocation);
    const barElement = unref(barRef);
    
    if (!barElement) return;
    
    const currentSegment = categories[segmentIndex];
    const nextSegment = isRightEdge ? categories[segmentIndex + 1] : categories[segmentIndex - 1];
    
    if (!nextSegment) return;
    
    const startX = event.clientX;
    const startCurrentValue = allocation[currentSegment.key as keyof PulseTimeAllocation] || 0;
    const startNextValue = allocation[nextSegment.key as keyof PulseTimeAllocation] || 0;
    
    // Get the actual bar width
    const barRect = barElement.getBoundingClientRect();
    const barWidth = barRect.width;
    
    function onMouseMove(e: MouseEvent) {
      const deltaX = e.clientX - startX;
      // Convert pixel movement to percentage, rounded to nearest 5%
      const deltaPercent = Math.round((deltaX / barWidth) * 100 / 5) * 5;
      
      let newCurrentValue, newNextValue;
      
      if (isRightEdge) {
        newCurrentValue = Math.max(5, Math.min(95, startCurrentValue + deltaPercent));
        newNextValue = Math.max(5, Math.min(95, startNextValue - deltaPercent));
      } else {
        newCurrentValue = Math.max(5, Math.min(95, startCurrentValue - deltaPercent));
        newNextValue = Math.max(5, Math.min(95, startNextValue + deltaPercent));
      }
      
      // Ensure the change sums to the same total
      const totalChange = (newCurrentValue - startCurrentValue) + (newNextValue - startNextValue);
      if (Math.abs(totalChange) <= 1) { // Allow for small rounding errors
        // Emit the updates to trigger the proper reactive flow
        emit('update-allocation', currentSegment.key, newCurrentValue);
        emit('update-allocation', nextSegment.key, newNextValue);
      }
    }
    
    function onMouseUp() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  return {
    onDragStart
  };
}
